import { createPortal } from 'react-dom'
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Loader2,
} from 'lucide-react'

interface SubmissionError {
  code: string
  message: string
  location?: string
}

interface PolledPeriod {
  periodIndex?: number
  correlationId?: string
  status?: string
  requiresPolling?: boolean
  errors?: SubmissionError[]
}

interface PeriodSubmission {
  periodIndex?: number
  periodLabel?: string
  periodStart?: string
  periodEnd?: string
  status?: string
  correlationId?: string
  hmrcReference?: string
  irMark?: string
  hmrcReceiptText?: string
  statusMessage?: string
  submittedAt?: string
  acceptedAt?: string
  lastPolledAt?: string
  accountsAttached?: boolean
  errors?: SubmissionError[]
}

interface HmrcStatusData {
  success?: boolean
  message?: string
  data?: {
    polled?: PolledPeriod[]
    submission?: {
      submissionId?: string
      correlationId?: string
      status?: string
      timestamp?: string
      message?: string
      requiresPolling?: boolean
      errors?: SubmissionError[]
    }
    form?: {
      id?: string
      status?: string
      submissionDate?: string
      periodSubmissions?: PeriodSubmission[]
      submissionDetails?: {
        submittedBy?: string
        submitterRole?: string
        submissionReference?: string
        hmrcAcknowledgement?: string
        statusMessage?: string
        lastPolledAt?: string
        softwareDetails?: {
          name?: string
          version?: string
          vendor?: string
        }
      }
    }
  }
}

interface Props {
  data: Record<string, unknown>
  onClose: () => void
  onRecheck: () => void
  isRechecking: boolean
}

const fmt = (iso?: string) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/** Try to extract a human-readable string from an error message that may be JSON. */
const parseErrorMessage = (raw: string): string => {
  try {
    const parsed = JSON.parse(raw)
    // { Error: { Text: '...' } }
    if (parsed?.Error?.Text) return parsed.Error.Text
    // { Error: [{ Text: '...' }] }
    if (Array.isArray(parsed?.Error)) {
      const texts = parsed.Error.map((e: { Text?: string }) => e.Text).filter(
        Boolean
      )
      if (texts.length) return texts.join(' ')
    }
    // { message: '...' }
    if (typeof parsed?.message === 'string') return parsed.message
  } catch {
    // not JSON — return as-is
  }
  return raw
}

const StatusBadge = ({ status }: { status: string | undefined }) => {
  const s = (status ?? '').toLowerCase()
  if (s === 'accepted') {
    return (
      <span className='inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800'>
        <CheckCircle className='h-3.5 w-3.5' />
        Accepted
      </span>
    )
  }
  if (s === 'rejected') {
    return (
      <span className='inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-800'>
        <XCircle className='h-3.5 w-3.5' />
        Rejected
      </span>
    )
  }
  if (s === 'pending' || s === 'processing' || s === 'submitted') {
    return (
      <span className='inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800'>
        <Clock className='h-3.5 w-3.5' />
        {status}
      </span>
    )
  }
  return (
    <span className='inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700'>
      {status ?? 'Unknown'}
    </span>
  )
}

const Row = ({
  label,
  value,
}: {
  label: string
  value?: string | undefined
}) => (
  <div className='flex flex-col gap-0.5'>
    <span className='text-xs text-gray-500'>{label}</span>
    <span className='text-sm font-medium text-gray-900 break-all'>
      {value ?? '—'}
    </span>
  </div>
)

export default function HmrcStatusModal({
  data,
  onClose,
  onRecheck,
  isRechecking,
}: Props) {
  const d = data as HmrcStatusData
  const sub = d.data?.submission
  const form = d.data?.form
  const details = form?.submissionDetails
  const errors = sub?.errors ?? []
  const polled = Array.isArray(d.data?.polled) ? d.data!.polled : []
  const periodSubmissions = Array.isArray(form?.periodSubmissions)
    ? form!.periodSubmissions
    : []
  const isLpoa = periodSubmissions.length > 1 || polled.length > 1

  // Aggregate-status colour. Prefer the form-level aggregate; fall back to the
  // legacy `submission.status` for non-LPOA filings.
  const aggregateStatus = form?.status ?? sub?.status
  const statusColor = (() => {
    const s = (aggregateStatus ?? '').toLowerCase()
    if (s === 'accepted') return 'border-green-200 bg-green-50'
    if (s === 'rejected') return 'border-red-200 bg-red-50'
    return 'border-amber-200 bg-amber-50'
  })()

  const modal = (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center p-4'>
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/50' onClick={onClose} />

      {/* Panel */}
      <div className='relative z-10 w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl'>
        {/* Header */}
        <div className='flex items-center justify-between rounded-t-2xl border-b border-gray-200 bg-white px-6 py-4 shrink-0'>
          <div>
            <h2 className='text-base font-bold text-gray-900'>
              HMRC Submission Status
            </h2>
            <p className='mt-0.5 text-xs text-gray-500'>
              {d.message ?? 'Status retrieved from HMRC'}
            </p>
          </div>
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={onRecheck}
              disabled={isRechecking}
              className='flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50'
            >
              {isRechecking ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : (
                <RefreshCw className='h-3 w-3' />
              )}
              Re-check
            </button>
            <button
              type='button'
              onClick={onClose}
              className='flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-500'
            >
              <X className='h-4 w-4' />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className='overflow-y-auto flex-1'>
          <div className='space-y-5 p-6'>
            {/* Status Banner */}
            {(sub?.status || aggregateStatus) && (
              <div
                className={`flex items-center justify-between rounded-xl border-2 p-4 ${statusColor}`}
              >
                <div>
                  <p className='text-xs font-medium text-gray-600'>
                    {isLpoa ? 'Aggregate HMRC Response' : 'HMRC Response'}
                  </p>
                  <p className='mt-0.5 text-sm text-gray-700'>
                    {sub?.message ?? aggregateStatus ?? sub?.status}
                  </p>
                </div>
                <StatusBadge status={aggregateStatus ?? sub?.status} />
              </div>
            )}

            {/* Per-period status (LPOA + non-LPOA when periodSubmissions is populated) */}
            {periodSubmissions.length > 0 && (
              <div className='rounded-xl border border-gray-200 bg-white p-4'>
                <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  {isLpoa ? `Periods (${periodSubmissions.length})` : 'Period'}
                </p>
                <div className='space-y-3'>
                  {periodSubmissions.map((ps, i) => (
                    <div
                      key={i}
                      className='rounded-lg border border-gray-200 p-3'
                    >
                      <div className='flex items-start justify-between gap-3 mb-2'>
                        <div>
                          <p className='text-sm font-semibold text-gray-900'>
                            {ps.periodLabel ??
                              `Period ${ps.periodIndex ?? i + 1}`}
                          </p>
                          {(ps.periodStart || ps.periodEnd) && (
                            <p className='text-[11px] text-gray-500'>
                              {fmt(ps.periodStart)} → {fmt(ps.periodEnd)}
                            </p>
                          )}
                        </div>
                        <StatusBadge status={ps.status} />
                      </div>
                      <div className='grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] text-gray-600'>
                        {ps.correlationId && (
                          <div>
                            <span className='text-gray-500'>Correlation:</span>{' '}
                            <span className='font-mono break-all'>
                              {ps.correlationId}
                            </span>
                          </div>
                        )}
                        {ps.hmrcReference && (
                          <div>
                            <span className='text-gray-500'>HMRC Ref:</span>{' '}
                            <span className='font-mono break-all'>
                              {ps.hmrcReference}
                            </span>
                          </div>
                        )}
                        {ps.irMark && (
                          <div>
                            <span className='text-gray-500'>IRmark:</span>{' '}
                            <span className='font-mono break-all'>
                              {ps.irMark}
                            </span>
                          </div>
                        )}
                        {typeof ps.accountsAttached === 'boolean' && (
                          <div>
                            <span className='text-gray-500'>
                              Accounts attached:
                            </span>{' '}
                            {ps.accountsAttached
                              ? 'Yes'
                              : 'No (filed with separate return)'}
                          </div>
                        )}
                        {ps.acceptedAt && (
                          <div>
                            <span className='text-gray-500'>Accepted:</span>{' '}
                            {fmt(ps.acceptedAt)}
                          </div>
                        )}
                        {ps.lastPolledAt && (
                          <div>
                            <span className='text-gray-500'>Last polled:</span>{' '}
                            {fmt(ps.lastPolledAt)}
                          </div>
                        )}
                      </div>
                      {ps.hmrcReceiptText && (
                        <p className='mt-2 rounded bg-gray-50 p-2 text-[11px] text-gray-700 italic leading-relaxed'>
                          {ps.hmrcReceiptText}
                        </p>
                      )}
                      {Array.isArray(ps.errors) && ps.errors.length > 0 && (
                        <div className='mt-2 rounded-lg border border-red-200 bg-red-50 p-2'>
                          <p className='text-[11px] font-semibold text-red-900 mb-1'>
                            Errors
                          </p>
                          <ul className='space-y-1'>
                            {ps.errors.map((e, j) => (
                              <li
                                key={j}
                                className='flex items-start gap-2 text-[11px] text-red-800'
                              >
                                <span className='font-mono rounded bg-red-100 px-1 py-0.5 text-[10px] text-red-700 shrink-0'>
                                  {e.code}
                                </span>
                                <span>{parseErrorMessage(e.message)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Latest poll snapshot (when periodSubmissions hasn't been hydrated yet) */}
            {periodSubmissions.length === 0 && polled.length > 0 && (
              <div className='rounded-xl border border-gray-200 bg-white p-4'>
                <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Latest poll
                </p>
                <div className='space-y-2'>
                  {polled.map((p, i) => (
                    <div
                      key={i}
                      className='flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2'
                    >
                      <div>
                        <p className='text-xs font-medium text-gray-900'>
                          Period {p.periodIndex ?? i + 1}
                        </p>
                        {p.correlationId && (
                          <p className='text-[10px] text-gray-500 font-mono break-all'>
                            {p.correlationId}
                          </p>
                        )}
                      </div>
                      <StatusBadge status={p.status} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div className='rounded-xl border border-red-200 bg-red-50 p-4'>
                <div className='mb-3 flex items-center gap-2'>
                  <AlertTriangle className='h-4 w-4 text-red-600' />
                  <p className='text-sm font-semibold text-red-900'>
                    Errors ({errors.length})
                  </p>
                </div>
                <div className='space-y-2'>
                  {errors.map((err, i) => (
                    <div
                      key={i}
                      className='flex items-start gap-3 rounded-lg bg-white p-3 border border-red-100'
                    >
                      <span className='rounded bg-red-100 px-1.5 py-0.5 text-xs font-mono font-bold text-red-700 shrink-0 mt-0.5'>
                        {err.code}
                      </span>
                      <p className='text-xs text-red-800 leading-relaxed'>
                        {parseErrorMessage(err.message)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submission IDs */}
            {sub && (
              <div className='rounded-xl border border-gray-200 bg-white p-4'>
                <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Submission
                </p>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  <Row label='Submission ID' value={sub.submissionId} />
                  <Row label='Correlation ID' value={sub.correlationId} />
                  <Row label='Timestamp' value={fmt(sub.timestamp)} />
                  <Row
                    label='Requires Polling'
                    value={
                      sub.requiresPolling === true
                        ? 'Yes'
                        : sub.requiresPolling === false
                          ? 'No'
                          : undefined
                    }
                  />
                </div>
              </div>
            )}

            {/* Form & Filing Details */}
            {(form || details) && (
              <div className='rounded-xl border border-gray-200 bg-white p-4'>
                <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Filing Details
                </p>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                  {form?.id && <Row label='Form ID' value={form.id} />}
                  {form?.submissionDate && (
                    <Row
                      label='Submission Date'
                      value={fmt(form.submissionDate)}
                    />
                  )}
                  {details?.submittedBy && (
                    <Row label='Submitted By' value={details.submittedBy} />
                  )}
                  {details?.submitterRole && (
                    <Row
                      label='Submitter Role'
                      value={
                        details.submitterRole === 'agent'
                          ? 'Tax Agent'
                          : 'Director'
                      }
                    />
                  )}
                  {details?.submissionReference && (
                    <Row
                      label='Submission Reference'
                      value={details.submissionReference}
                    />
                  )}
                  {details?.hmrcAcknowledgement && (
                    <Row
                      label='HMRC Acknowledgement'
                      value={details.hmrcAcknowledgement}
                    />
                  )}
                  {details?.lastPolledAt && (
                    <Row
                      label='Last Polled'
                      value={fmt(details.lastPolledAt)}
                    />
                  )}
                  {details?.statusMessage && (
                    <Row label='Status Message' value={details.statusMessage} />
                  )}
                </div>
              </div>
            )}

            {/* Software Details */}
            {details?.softwareDetails && (
              <div className='rounded-xl border border-gray-200 bg-white p-4'>
                <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Software
                </p>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                  <Row label='Name' value={details.softwareDetails.name} />
                  <Row label='Vendor' value={details.softwareDetails.vendor} />
                  <Row
                    label='Version'
                    value={details.softwareDetails.version}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className='rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-3 shrink-0'>
          <button
            type='button'
            onClick={onClose}
            className='w-full rounded-lg bg-[#001B38] py-2.5 text-sm font-medium text-white hover:bg-[#002a50] transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
